import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../../utils/enums';

const initialState = {
  signs: null,
  isLoading: false,
  error: null,
  signImages: null,
  signImagesLoading: false,
  signImagesError: null,

  selectedDrive: null,
  gpsData: [],
  GpsDataLoading: false,
  gpsDataError: null,
  events: [],
  eventsStatus: RequestStatus.IDLE,
  eventsError: null,
  imageUrl: null,
  imageUrlError: null,
  imageUrlLoading: false,

  removeEventLoading: false,
  removeEventError: null,
  changeSignLoading: false,
  changeSignError: null,
  repositionSignsLoading: false,
  repositionSignsError: null,
  editDriveNameLoading: false,
  editDriveNameError: null,
  nearestGpsDataLoading: false,
  nearestGpsDataError: null,
  nearestGpsData: {},
  addNewSign: [],
  addNewSignLoading: false,
  addNewSignError: null,
};

const signSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    getSignsStart: (state) => {
      state.isLoading = true;
      state.error = null;
      state.signs = null;
      state.lastFetched = null;
    },
    getSignsSuccess: (state, action) => {
      state.isLoading = false;
      state.signs = action.payload.data;
      state.lastFetched = Date.now();
      state.error = null;
    },
    getSignsFailure: (state, action) => {
      state.isLoading = false;
      state.signs = null;
      state.lastFetched = null;
      state.error = action.payload.message;
    },
    getSignImagesStart: (state) => {
      state.signImagesLoading = true;
      state.signImagesError = null;
      state.signImages = null;
    },
    getSignImagesSuccess: (state, action) => {
      state.signImagesLoading = false;
      state.signImages = action.payload.data;
      state.signImagesError = null;
    },
    getSignImagesFailure: (state, action) => {
      state.signImagesLoading = false;
      state.signImagesError = action.payload.message;
    },
    //GPS Data Reducers
    getGPSDataStart: (state) => {
      state.GpsDataLoading = true;
      state.gpsDataError = null;
      state.gpsData = [];
    },
    getGPSDataSuccess: (state, action) => {
      state.GpsDataLoading = false;
      state.gpsData = action.payload;
      state.gpsDataError = null;
    },
    getGPSDataFailure: (state, action) => {
      state.GpsDataLoading = false;
      state.gpsDataError = action.payload.message;
    },
    // Events Reducers
    getEventsStart: (state) => {
      state.eventsStatus = RequestStatus.LOADING;
      state.eventsError = null;
      state.events = [];
      state.signImages = null;
    },
    getEventsSuccess: (state, action) => {
      state.eventsStatus = RequestStatus.SUCCEEDED;
      state.events = action.payload.data;
      state.eventsError = null;
    },
    getEventsFailure: (state, action) => {
      state.eventsStatus = RequestStatus.FAILED;
      state.eventsError = action.payload.message;
    },
    resetEventsState: (state) => {
      state.events = [];
      state.eventsStatus = RequestStatus.IDLE;
      state.eventsError = null;
      state.signImages = null;
    },
    removeEventStart: (state) => {
      state.removeEventError = null;
      state.removeEventLoading = true;
    },
    removeEventSuccess: (state) => {
      state.removeEventError = null;
      state.removeEventLoading = false;
    },
    removeEventFailure: (state, action) => {
      state.removeEventError = action.payload.message;
      state.removeEventLoading = false;
    },
    changeSignStart: (state) => {
      state.changeSignError = null;
      state.changeSignLoading = true;
    },
    changeSignSuccess: (state) => {
      state.changeSignError = null;
      state.changeSignLoading = false;
    },
    changeSignFailure: (state, action) => {
      state.changeSignError = action.payload.message;
      state.changeSignLoading = false;
    },
    repositionSignsStart: (state) => {
      state.repositionSignsError = null;
      state.repositionSignsLoading = true;
    },
    repositionSignsSuccess: (state) => {
      state.repositionSignsError = null;
      state.repositionSignsLoading = false;
    },
    repositionSignsFailure: (state, action) => {
      state.repositionSignsError = action.payload.message;
      state.repositionSignsLoading = false;
    },
    editDriveNameStart: (state) => {
      state.editDriveNameError = null;
      state.editDriveNameLoading = true;
    },
    editDriveNameSuccess: (state) => {
      state.editDriveNameError = null;
      state.editDriveNameLoading = false;
    },
    editDriveNameFailure: (state, action) => {
      state.editDriveNameError = action.payload.message;
      state.editDriveNameLoading = false;
    },
    getNearestGPSDataStart: (state) => {
      state.nearestGpsData = {};
      state.nearestGpsDataError = null;
      state.nearestGpsDataLoading = true;
    },
    getNearestGPSDataSuccess: (state, action) => {
      state.nearestGpsData = action.payload.data;
      state.nearestGpsDataError = null;
      state.nearestGpsDataLoading = false;
    },
    getNearestGPSDataFailure: (state, action) => {
      state.nearestGpsData = {};
      state.nearestGpsDataError = action.payload.message;
      state.nearestGpsDataLoading = false;
    },
    clearNearestGPSData: (state) => {
      state.nearestGpsData = {};
    },
    addNewSignStart: (state) => {
      state.addNewSign = [];
      state.addNewSignError = null;
      state.addNewSignLoading = true;
    },
    addNewSignSuccess: (state, action) => {
      state.addNewSign = action.payload.data;
      state.addNewSignError = null;
      state.addNewSignLoading = false;
    },
    addNewSignFailure: (state, action) => {
      state.addNewSign = [];
      state.addNewSignError = action.payload.message;
      state.addNewSignLoading = false;
    },
    clearNewSign: (state) => {
      state.addNewSign = [];
    },
    getImageUrlStart: (state) => {
      state.imageUrlLoading = true;
      state.imageUrlError = null;
      state.imageUrl = null;
    },
    getImageUrlSuccess: (state, action) => {
      state.imageUrlLoading = false;
      state.imageUrl = action.payload.data;
    },
    getImageUrlFailure: (state, action) => {
      state.imageUrlLoading = false;
      state.imageUrlError = action.payload.message;
      state.imageUrl = null;
    },
    resetImageUrlState: (state) => {
      state.imageUrl = null;
      state.imageUrlLoading = false;
      state.imageUrlError = null;
    },
  },
});

export const {
  getSignsStart,
  getSignsSuccess,
  getSignsFailure,
  getSignImagesStart,
  getSignImagesSuccess,
  getSignImagesFailure,
  getGPSDataStart,
  getGPSDataSuccess,
  getGPSDataFailure,
  getEventsStart,
  getEventsSuccess,
  getEventsFailure,
  resetEventsState,
  removeEventStart,
  removeEventSuccess,
  removeEventFailure,
  changeSignStart,
  changeSignSuccess,
  changeSignFailure,
  repositionSignsStart,
  repositionSignsSuccess,
  repositionSignsFailure,
  editDriveNameStart,
  editDriveNameSuccess,
  editDriveNameFailure,
  getNearestGPSDataStart,
  getNearestGPSDataSuccess,
  getNearestGPSDataFailure,
  clearNearestGPSData,
  addNewSignStart,
  addNewSignSuccess,
  addNewSignFailure,
  clearNewSign,
  getImageUrlStart,
  getImageUrlSuccess,
  getImageUrlFailure,
  resetImageUrlState,
} = signSlice.actions;

export default signSlice.reducer;
